import { buildLandingPagePath } from '@/features/navigation/components/LandingPageLink'
import { addOrganizationScopeToPath } from '../utils/addOrganizationScopeToPath'
import { useMutation } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { setActiveOrgSlugCookie, useApiQueryClient } from '@joyful/ui/app'
import type { components } from '@joyful/openapi/types.gen'

interface SetActiveAccountMutationVariables {
  account: Pick<components['schemas']['CurrentUserAccountRead'], 'slug'>
  targetPath?: string
}

export function useSetActiveAccount({
  onSuccess,
}: { onSuccess?: () => void } = {}) {
  const { queryClient } = useApiQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  return useMutation({
    mutationFn: async (variables: SetActiveAccountMutationVariables) => {
      setActiveOrgSlugCookie(variables.account.slug)
      const currentPath = `${location.pathname}${location.search}${location.hash}`
      const targetPath = variables.targetPath ?? currentPath
      const scopedPath = addOrganizationScopeToPath({
        orgSlug: variables.account.slug,
        path: targetPath,
      })

      if (scopedPath === currentPath) {
        // instead of in-place navigation, go to landing page
        void navigate(
          buildLandingPagePath({
            orgSlug: variables.account.slug,
          }),
        )
      } else {
        void navigate(scopedPath)
      }

      await queryClient.invalidateQueries()
    },
    onSuccess,
  })
}
